import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRepo } from 'pinia-orm';
import { Avatar } from '../models/Avatar';
import {
  useStrapiGet,
  useStrapiPost,
  useStrapiPut,
  useStrapiDelete,
} from 'src/shared/composables/useStrapiApi';
import { useEventStore } from 'src/stores/event';
import { Notify } from 'quasar';
import { useI18n } from 'vue-i18n';
import { mdiCheckCircle, mdiAlertCircle } from '@quasar/extras/mdi-v7';
import type { AvatarConfig } from '../types/avatarConfig';
import type { ErrorResponse } from 'src/types/Response';

export function useUserAvatar(userId?: number | null) {
  const { t } = useI18n();
  const avatarRepo = useRepo(Avatar);
  const eventStore = useEventStore();
  const isSaving = ref(false);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const userAvatar = computed(() => {
    if (!userId) return null;
    return avatarRepo.where('user_id', userId).first();
  });

  watch(
    () => userAvatar.value,
    (newAvatar) => {
      if (newAvatar && newAvatar.avatar_config) {
        eventStore.emit('avatar:config-loaded', {
          config: newAvatar.avatar_config,
        });
      }
    },
    { immediate: true },
  );

  async function fetchUserAvatar() {
    if (!userId) return null;

    try {
      isLoading.value = true;
      error.value = null;

      const {
        data,
        error: apiError,
        execute,
      } = useStrapiGet(`users/${userId}`, {
        populate: ['avatar'],
      });

      await execute();

      if (apiError.value) {
        throw new Error(
          (apiError.value as ErrorResponse)?.message ||
            'Failed to fetch avatar',
        );
      }

      if (data.value?.data?.avatar) {
        const avatarData = {
          ...data.value.data.avatar,
          user_id: userId, // ensure relation tracked locally
        };
        avatarRepo.save(avatarData);
        return avatarData;
      }

      return null;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to fetch avatar';
      console.error('Avatar fetch error:', e);
      return null;
    } finally {
      isLoading.value = false;
    }
  }

  async function saveAvatar(avatarConfig: AvatarConfig) {
    if (!userId) {
      Notify.create({
        message: t('USER_NOT_LOGGED_IN'),
        color: 'warning',
      });
      return false;
    }

    try {
      isSaving.value = true;
      error.value = null;

      const avatarData = {
        style: avatarConfig.style,
        seed: avatarConfig.seed,
        avatar_config: avatarConfig,
        avatar_svg: avatarConfig.svg,
      };

      const existing = userAvatar.value;

      if (existing && existing.documentId) {
        const {
          data,
          execute,
          error: apiError,
        } = useStrapiPut(`avatars/${existing.documentId}`, {
          data: avatarData,
        });

        await execute();

        if (apiError.value) {
          throw new Error(
            (apiError.value as ErrorResponse)?.message ||
              'Failed to update avatar',
          );
        }

        if (data.value?.data) {
          avatarRepo.save({ ...data.value.data, user_id: userId });
        }
      } else {
        const {
          data,
          execute,
          error: apiError,
        } = useStrapiPost('avatars', { data: avatarData });

        await execute();

        if (apiError.value) {
          throw new Error(
            (apiError.value as ErrorResponse)?.message ||
              'Failed to create avatar',
          );
        }

        if (data.value?.data) {
          avatarRepo.save({ ...data.value.data, user_id: userId });
        }
      }

      Notify.create({
        message: t('AVATAR_SAVED_SUCCESS'),
        color: 'positive',
        icon: mdiCheckCircle,
      });

      return true;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to save avatar';

      Notify.create({
        message: t('AVATAR_SAVED_ERROR'),
        caption: error.value,
        color: 'negative',
        icon: mdiAlertCircle,
      });

      console.error('Avatar save error:', e);
      return false;
    } finally {
      isSaving.value = false;
    }
  }

  function loadAvatar(): AvatarConfig | null {
    return userAvatar.value?.avatar_config || null;
  }

  async function deleteAvatar() {
    const existing = userAvatar.value;
    if (!existing) return true;

    try {
      const { execute, error: apiError } = useStrapiDelete(
        `avatars/${existing.id ?? existing.documentId}`,
      );
      await execute();
      if (apiError.value) {
        throw new Error(
          (apiError.value as ErrorResponse)?.message ||
            'Failed to delete avatar',
        );
      }

      avatarRepo.destroy(existing.id);

      Notify.create({
        message: t('AVATAR_DELETED_SUCCESS'),
        color: 'positive',
        icon: mdiCheckCircle,
      });

      return true;
    } catch (e) {
      console.error('Avatar delete error:', e);
      Notify.create({
        message: t('AVATAR_DELETED_ERROR'),
        color: 'negative',
        icon: mdiAlertCircle,
      });
      return false;
    }
  }

  function handleAvatarSaveRequest(payload: { config: AvatarConfig }) {
    void saveAvatar(payload.config);
  }

  let unsubscribe: (() => void) | null = null;

  onMounted(() => {
    unsubscribe = eventStore.on(
      'avatar:save-requested',
      handleAvatarSaveRequest,
    );
  });

  onUnmounted(() => {
    if (unsubscribe) {
      unsubscribe();
    }
  });

  return {
    isSaving,
    isLoading,
    error,
    userAvatar,
    fetchUserAvatar,
    saveAvatar,
    loadAvatar,
    deleteAvatar,
  };
}
